import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import DeleteCompanyJob from "./partials/DeleteCompanyCompetency/DeleteCompanyCompetency";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";
import {
  ICompanyCompetency,
  ICompanyCompetencyInput,
} from "models/companyCompetencies";
import {
  addCompanyCompetencies,
  CompanyCompetencyCells,
  deleteCompanyCompetencies,
  editCompanyCompetencies,
} from "framework/companyCompetencies";
import {  importCompetency } from "framework/competencies";

import EditCompanyCompetency from "./partials/EditCompanyCompetency/EditCompanyCompetency";
import { UserRoleName } from "types/auth";
import { addCorporateCompetencies, deleteCorporateCompetencies, editCorporateCompetencies, getCorporateCompetencies } from "framework/corporateCompetencies";
import AddCorporateCompetenices from "./partials/AddCorporateCompetency/AddCorporateCompetencies";
import { deleteCorporate, editCorporate } from "framework/corporates";

interface Props {
  feature: Feature;
}

const CorporateCompetencies: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);

  const [FilterCompanyCompetencies, setFilterCompanyCompetencies] = useState<
    ICompanyCompetency[]
  >([] as ICompanyCompetency[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyCompetencies, setSelectedCompanyCompetencies] =
    useState<ICompanyCompetency>({} as ICompanyCompetency);
    const [inputObj, setInputObj] = useState<{name:string,nameAr:string}>({} as {name:string,nameAr:string});
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors,roleName } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCorporateMutation = useMutation({
    mutationFn: (createInput: {name:string,nameAr:string}[]) => {
      return addCorporateCompetencies(createInput);
    },
  });

  const editCorporateMutation = useMutation({
    mutationFn: (createInput: ICompanyCompetencyInput) => {
      return editCorporateCompetencies(
        createInput,
        selectedCompanyCompetencies.id
      );
    },
  });

  const deleteCorporateMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCorporateCompetencies(id);
    },
  });

  const importCompetenciesMutation = useMutation({
    mutationFn: () => {
      return importCompetency();
    },
  });


  const {
    loading,
    data: CompanyCompetencies,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCorporateCompetencies(),
    fetchPermission: () => getPermission(50),
    section: "Company Competencies",
    onSuccess: (data) => setFilterCompanyCompetencies(data),
  });


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );


    const handleChange = (name: string, value: any) => {
      setInputObj((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

  const handleOpenAdd = () => setOpenAdd(true);

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedCompanyCompetencies({} as ICompanyCompetency);
  };

  const handleOpenEdit = (Country: ICompanyCompetency) => {
    setSelectedCompanyCompetencies(Country);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyCompetencies({} as ICompanyCompetency);
  };

  const handleOpenDelete = (Country: ICompanyCompetency) => {
    setSelectedCompanyCompetencies(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyCompetencies({} as ICompanyCompetency);
  };

  const getBodyCells = (row: ICompanyCompetency) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async (data:{name:string,nameAr:string}[]) => {
    try {
      setLoading2(true);

      await addCorporateMutation.mutateAsync(data);
      setAlert({
        open: true,
        message: "Corporate Competency added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async (data: ICompanyCompetencyInput) => {
    try {
      setLoading2(true);

      await editCorporateMutation.mutateAsync({
        id: selectedCompanyCompetencies.id,
        name: data.name || selectedCompanyCompetencies.name,
        nameAr: data.nameAr || selectedCompanyCompetencies.nameAr,
      });
      setAlert({
        open: true,
        message: "Corporate Competency Edited successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCorporateMutation.mutateAsync(
        selectedCompanyCompetencies.id
      );
      setAlert({
        open: true,
        message: "Corporate Competency deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleImport = async () => {
    try {
      setLoading2(true);
    
      await importCompetenciesMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Competency successfully imported from the master list ",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyCompetencies(CompanyCompetencies!);
    let filtered = CompanyCompetencies!.filter((competency) =>
      competency.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterCompanyCompetencies(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />

            {permission?.hasModifyPermission && (
              <Tooltip title="Add Competencies">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {roleName === UserRoleName.COMPANY_ADMIN && <div style={{ display: "flex", justifyContent: "flex-end" }}>
  <Button
    variant="contained" onClick={handleImport} >
      {t('T_import_from_master')}
    </Button>
</div>}

        {permission?.hasReadPermission! &&
          FilterCompanyCompetencies.length > 0 && (
            <List
              headCells={CompanyCompetencyCells}
              data={FilterCompanyCompetencies}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterCompanyCompetencies ||
          FilterCompanyCompetencies.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_competency_header")}
              message={t("T_no_competency_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCorporateCompetenices
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
        />
      )}

      {openEdit && (
        <EditCompanyCompetency
          competencies={[]}
          open={openEdit}
          loading={loading2}
          selectedCompetency={selectedCompanyCompetencies}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
        />
      )}
      {openDelete && (
        <DeleteCompanyJob
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CorporateCompetencies;
